// Local fixtures so the app renders (and CSP can be verified) without auth or a
// live backend: open index.html?mock. Deployed (no ?mock) it hits the real API
// via the shared db client. Shapes are partial — cast to the shared types; only
// the fields the app reads are populated.
import type { Transaction, Budget, DebtView, SeriesPoint, Category, PaymentView, PaymentSuggestion, ReconcilePoint } from '@lib/db';
import { UNCAT } from '@lib/category';
import { getCurrentMonth, changeMonth, getPayCycleRange } from '@lib/month';
import { formatDate } from '@lib/format';

export function mockCategories(): Category[] {
	const c = (name: string, type: string): Category => ({ name, type }) as unknown as Category;
	return [
		c('Income', 'Income'),
		c('Mortgage', 'Mandatory Costs'),
		c('Food & Drink', 'Everyday'),
		c('Transport', 'Everyday'),
		c('Utilities', 'Mandatory Costs'),
	];
}

const tx = (id: string, amount: number, category: string, description: string): Transaction =>
	({ pk: 'MONTH#2026-06', sk: `TX#${id}`, txId: id, accountId: 'a1', date: '2026-06-20', description, amount, type: amount > 0 ? 'CREDIT' : 'DEBIT', category, isTransfer: false, excludeFromBudget: false, pending: false }) as Transaction;

const trajectory: SeriesPoint[] = [
	{ month: '2026-01', value: 9000 },
	{ month: '2026-02', value: 7600 },
	{ month: '2026-03', value: 6100 },
	{ month: '2026-04', value: 4700 },
	{ month: '2026-05', value: 3000 },
	{ month: '2026-06', value: 1500 },
];

export function mockTransactions(): Transaction[] {
	// Variants cover the TransactionCard display surface the smoke asserts:
	// merchant+meta (3), pending (6), suggestion on an uncategorised tx (7),
	// transfer (8), split with a line note (9).
	return [
		tx('1', 6200, 'Income', 'Salary'),
		tx('2', -1850, 'Mortgage', 'Home loan'),
		{ ...tx('3', -420, 'Food & Drink', 'COUNTDOWN AKL 4821'), merchant: { name: 'Countdown' }, meta: { particulars: 'Visa', code: 'POS', reference: '4821' } },
		tx('4', -180, 'Food & Drink', 'New World'),
		tx('5', -95, 'Transport', 'Z Energy'),
		{ ...tx('6', -260, 'Utilities', 'Power'), pending: true },
		{ ...tx('7', -55, UNCAT, 'Unknown EFTPOS'), suggestedCategory: 'Food & Drink', suggestedConfidence: 0.8 },
		{ ...tx('8', -200, 'Transfers', 'To savings'), isTransfer: true },
		{ ...tx('9', -55, 'SPLIT', 'Mixed shop'), splits: [{ category: 'Food & Drink', amount: -30, note: 'groceries' }, { category: 'Transport', amount: -25 }] },
	] as Transaction[];
}

export function mockBudget(): Budget {
	return {
		pk: 'BUDGET#2026-06',
		sk: 'PLAN',
		plannedIncome: 6200,
		incomeSchedules: [{ name: 'Salary', category: 'Income', amount: 6200, frequency: 'monthly', monthlyDay: 20 }],
		categoryBudgets: { Mortgage: 1850, 'Food & Drink': 500, Transport: 150, Utilities: 300 },
	} as unknown as Budget;
}

// Clone-source variant: the previous month's budget with breakdown lines, one
// of them a one-off inside that earlier cycle, so the smoke can pin that
// cloning keeps recurring lines but drops stale one-offs.
export function mockCloneSourceBudget(): Budget {
	const prevMonth = changeMonth(getCurrentMonth(), -1);
	return {
		...mockBudget(),
		pk: `BUDGET#${prevMonth}`,
		categoryLines: {
			Utilities: [
				{ name: 'Power', amount: 75, frequency: 'weekly', weeklyDay: 1 },
				{ name: 'Boiler service', amount: 120, frequency: 'one-off', oneOffDate: formatDate(getPayCycleRange(prevMonth).start) },
			],
		},
	} as unknown as Budget;
}

// Smoke-flippable variant: a blank current month (no budget, no categorised
// spend) with a saved budget in an earlier month, so the clone-previous-budget
// offer + apply flow is exercisable offline.
export const mockState = { blankBudget: false };

// Facility reconcile history with just enough statefulness that the smoke can
// submit a reconcile (via the mock write echo) and see it after a reload.
const facilityReconciles: ReconcilePoint[] = [];
export function mockAddReconcile(balance: number, opts: { date?: string; statementKey?: string; statementName?: string } = {}): void {
	facilityReconciles.unshift({ date: opts.date || '2026-06-20', balance, statementKey: opts.statementKey, statementName: opts.statementName });
}
export function mockRemoveReconcile(date: string): void {
	const i = facilityReconciles.findIndex((r) => r.date === date);
	if (i !== -1) facilityReconciles.splice(i, 1);
}

export function mockDebts(): DebtView[] {
	return [
		{
			id: 'd1',
			name: 'Gem Visa',
			debtType: 'credit_card',
			debtClass: 'consumer',
			legacyBalanceStart: 9000,
			legacyRemaining: 1500,
			legacyCategory: 'Legacy debt',
			targetBalance: 0,
			trajectory,
			unreconciledPayments: [mockUnreconciledPayment()],
		} as unknown as DebtView,
		{
			id: 'd2',
			name: 'Interest-free sofa',
			debtType: 'interest_free_facility',
			debtClass: 'consumer',
			dataSource: 'hybrid',
			originalPrincipal: 5000,
			currentBalance: 3200,
			minimumPayment: 200,
			paymentFrequency: 'monthly',
			paymentCategory: 'Utilities',
			startDate: '2026-01-15',
			endDate: '2027-07-15',
			targetBalance: 0,
			trajectory,
			reconciles: facilityReconciles.slice(),
			payments: [],
		} as unknown as DebtView,
	];
}

// An unreconciled card payment + the suggestion the API would return for it,
// kept mutually consistent (purchase 300 + legacy 200 = magnitude 500) so the
// reconcile editor's suggested defaults validate — the smoke asserts Save is
// enabled, guarding the validator wiring (e.g. magnitude vs signed amount).
export function mockUnreconciledPayment(): PaymentView {
	return {
		pk: 'MONTH#2026-06',
		sk: 'TX#pay1',
		date: '2026-06-18',
		description: 'Payment received - thank you',
		amount: -500,
		negatingCurrent: 0,
		carriedPaydown: 0,
		legacyPaydown: 0,
		overpayment: 0,
		minimumPortion: 0,
		extraPortion: 0,
	} as PaymentView;
}

export function mockPaymentSuggestion(): PaymentSuggestion {
	const charge = {
		pk: 'MONTH#2026-06',
		sk: 'TX#c1',
		date: '2026-06-05',
		description: 'Countdown',
		amount: -300,
		unpaid: 300,
		category: 'Food & Drink',
	};
	return {
		magnitude: 500,
		unpaidPool: [charge],
		allocations: [
			{ kind: 'purchase', targetPk: charge.pk, targetSk: charge.sk, amount: 300 },
			{ kind: 'legacy', amount: 200 },
		],
		legacyRemaining: 1500,
		role: 'minimum',
		minimumPortion: 500,
		extraPortion: 0,
	} as PaymentSuggestion;
}
